// frontend/my-react-app/src/pages/MedicationForm.jsx
// Created: 2026-03-10
// Updated: 2026-03-10 — Medication Name required and first; Display Name optional and second; checkbox near Medication Name
// Updated: 2026-03-10 — helper text darker (#6b7280); freq buttons horizontal; tz note gets clock icon; optional section relabelled
// Updated: 2026-03-10 — add Header for consistent nav; section colors blue → green
// Add medication form — 4 sections (identity, dosage, schedule, optional details).
// Props-based: petId, petName, userTimezone, onSave. No usePet inside — reusable for Phase 2 onboarding.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import { slotTo24hr, parse24hrToSlot, formatTimezoneLabel } from '../utils/timeUtils';
import './MedicationForm.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const FORMAT_OPTIONS = [
  'pill', 'tablet', 'capsule', 'liquid', 'drops',
  'spray', 'patch', 'cream', 'injection', 'other',
];

const STRENGTH_UNIT_OPTIONS = ['mg', 'mL', 'mcg', 'mEq', 'IU', 'units', 'Other'];

const DAYS = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 7 },
];

const HOURS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINUTES = ['00', '15', '30', '45'];

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Every day' },
  { value: 'specific days', label: 'Specific days of the week' },
  { value: 'as_needed', label: 'As needed' },
];

function emptySlot() {
  return { hour: 8, minute: '00', period: 'AM' };
}

function mapFrequency(fp) {
  if (fp === 'weekly' || fp === 'custom') return 'specific days';
  if (fp === 'daily' || fp === 'specific days' || fp === 'as_needed') return fp;
  return 'daily';
}

function optionalHasData(v) {
  if (!v) return false;
  return (
    v.strength_amount != null ||
    (v.strength_unit && v.strength_unit !== 'Other') ||
    (v.strength_unit_custom && String(v.strength_unit_custom).trim()) ||
    (v.special_instructions && String(v.special_instructions).trim()) ||
    v.start_date ||
    v.end_date
  );
}

function dateValue(val) {
  if (!val) return '';
  const s = typeof val === 'string' ? val : String(val);
  return s.slice(0, 10);
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function MedicationForm({ petId, petName, userTimezone, onSave, initialValues, onStop }) {
  const navigate = useNavigate();
  const v = initialValues;

  // Section 1 — Identity
  const [displayName, setDisplayName] = useState(v?.display_name ?? '');
  const [medicationName, setMedicationName] = useState(v?.name ?? '');
  const [useAsDisplayName, setUseAsDisplayName] = useState(false);

  // Section 2 — Dosage
  const [format, setFormat] = useState(v?.medication_format ?? '');
  const [quantity, setQuantity] = useState(
    v?.quantity_to_take != null ? String(parseFloat(v.quantity_to_take)) : ''
  );

  // Section 3 — Schedule
  const [frequency, setFrequency] = useState(v ? mapFrequency(v.frequency_pattern) : 'daily');
  const [timeSlots, setTimeSlots] = useState(() => {
    const times = v?.scheduled_times;
    if (times && Array.isArray(times) && times.length > 0) {
      return times.map(parse24hrToSlot);
    }
    return [emptySlot()];
  });
  const [selectedDays, setSelectedDays] = useState(
    v?.days_of_week && Array.isArray(v.days_of_week) ? [...v.days_of_week] : []
  );

  // Section 4 — Optional details
  const [showOptional, setShowOptional] = useState(v ? optionalHasData(v) : false);
  const [strengthAmount, setStrengthAmount] = useState(
    v?.strength_amount != null ? String(parseFloat(v.strength_amount)) : ''
  );
  const [strengthUnit, setStrengthUnit] = useState(
    v?.strength_unit_custom ? 'Other' : (v?.strength_unit ?? '')
  );
  const [strengthUnitCustom, setStrengthUnitCustom] = useState(v?.strength_unit_custom ?? '');
  const [specialInstructions, setSpecialInstructions] = useState(v?.special_instructions ?? '');
  const [startDate, setStartDate] = useState(dateValue(v?.start_date));
  const [endDate, setEndDate] = useState(dateValue(v?.end_date));

  // Form state
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [confirmingStop, setConfirmingStop] = useState(false);

  // ── Frequency switching ──────────────────────────────────────────────────

  function handleFrequencyChange(newFreq) {
    if (newFreq === 'as_needed') {
      setTimeSlots([emptySlot()]);
      setSelectedDays([]);
    } else if (frequency === 'as_needed') {
      setTimeSlots([emptySlot()]);
      setSelectedDays([]);
    } else if (newFreq === 'daily' && frequency === 'specific days') {
      setSelectedDays([]);
    }
    setFrequency(newFreq);
    setErrors(prev => ({ ...prev, frequency: null, times: null, days: null }));
  }

  // ── Time slots ───────────────────────────────────────────────────────────

  function updateSlot(index, field, value) {
    setTimeSlots(prev => prev.map((slot, i) =>
      i === index ? { ...slot, [field]: value } : slot
    ));
    setErrors(prev => ({ ...prev, times: null }));
  }

  function addSlot() {
    setTimeSlots(prev => [...prev, emptySlot()]);
  }

  function removeSlot(index) {
    setTimeSlots(prev => prev.filter((_, i) => i !== index));
  }

  // ── Day chips ────────────────────────────────────────────────────────────

  function toggleDay(dayValue) {
    setSelectedDays(prev =>
      prev.includes(dayValue)
        ? prev.filter(d => d !== dayValue)
        : [...prev, dayValue]
    );
    setErrors(prev => ({ ...prev, days: null }));
  }

  // ── "Use as display name" checkbox ──────────────────────────────────────

  function handleUseAsDisplayName(checked) {
    setUseAsDisplayName(checked);
    if (checked) setDisplayName(medicationName);
    setErrors(prev => ({ ...prev, displayName: null }));
  }

  function handleMedicationNameChange(value) {
    setMedicationName(value);
    if (useAsDisplayName) setDisplayName(value);
  }

  // ── Validation ───────────────────────────────────────────────────────────

  function validate() {
    const newErrors = {};

    if (!medicationName.trim()) {
      newErrors.medicationName = 'Medication name is required.';
    }
    if (!format) {
      newErrors.format = 'Please select a format.';
    }
    const qty = parseFloat(quantity);
    if (!quantity || isNaN(qty) || qty <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0.';
    }
    if (frequency !== 'as_needed') {
      if (timeSlots.length === 0) {
        newErrors.times = 'Please add at least one time.';
      }
    }
    if (frequency === 'specific days' && selectedDays.length === 0) {
      newErrors.days = 'Please select at least one day.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // ── Submit ───────────────────────────────────────────────────────────────

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    setSaveError(null);

    const scheduledTimes =
      frequency !== 'as_needed'
        ? timeSlots.map(slotTo24hr)
        : null;

    const daysOfWeek =
      frequency === 'specific days' && selectedDays.length > 0
        ? [...selectedDays].sort((a, b) => a - b)
        : null;

    // display_name: null when checkbox checked (app falls back to name); otherwise use the typed value
    const displayNameValue = useAsDisplayName ? null : (displayName.trim() || null);

    const formData = {
      name: medicationName.trim(),
      display_name: displayNameValue,
      medication_format: format,
      quantity_to_take: parseFloat(quantity),
      frequency_pattern: frequency,
      scheduled_times: scheduledTimes,
      days_of_week: daysOfWeek,
      strength_amount: strengthAmount ? parseFloat(strengthAmount) : null,
      strength_unit: strengthUnit && strengthUnit !== 'Other' ? strengthUnit : null,
      strength_unit_custom: strengthUnit === 'Other' ? (strengthUnitCustom.trim() || null) : null,
      special_instructions: specialInstructions.trim() || null,
      start_date: startDate || null,
      end_date: endDate || null,
    };

    try {
      await onSave(formData);
      setSaveSuccess(true);
      setTimeout(() => navigate('/meds'), 1500);
    } catch {
      setSaveError('Something went wrong. Your changes weren\'t saved. Try again.');
    } finally {
      setIsSaving(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const tzLabel = userTimezone ? formatTimezoneLabel(userTimezone) : null;
  const showTimeFields = frequency !== 'as_needed';
  const showDayChips = frequency === 'specific days';

  return (
    <div className="med-form-page">
      <Header />
      <div className="med-form-topbar">
        <button
          className="med-form-back"
          onClick={() => navigate('/meds')}
          type="button"
        >
          ← Back
        </button>
      </div>

      <div className="med-form-content">
        <div className="med-form-inner">
          <h1 className="med-form-title">
            {v ? 'Edit Medication' : `Add Medication${petName ? ` for ${petName}` : ''}`}
          </h1>

          <form onSubmit={handleSubmit} noValidate>

            {/* ── Section 1: Identity ─────────────────────────────── */}
            <div className="med-form-section">
              <div className="med-form-section-header">
                <span className="med-form-section-title">Medication</span>
              </div>
              <div className="med-form-section-body">

                <div className="med-form-field">
                  <label className="med-form-label" htmlFor="medicationName">
                    Medication name <span className="med-form-required">*</span>
                  </label>
                  <input
                    id="medicationName"
                    className={`med-form-input${errors.medicationName ? ' med-form-input--error' : ''}`}
                    type="text"
                    placeholder="e.g. Atenolol"
                    value={medicationName}
                    onChange={e => {
                      handleMedicationNameChange(e.target.value);
                      setErrors(prev => ({ ...prev, medicationName: null }));
                    }}
                  />
                  {errors.medicationName && (
                    <span className="med-form-error">{errors.medicationName}</span>
                  )}
                  <label className="med-form-checkbox-label">
                    <input
                      type="checkbox"
                      checked={useAsDisplayName}
                      onChange={e => handleUseAsDisplayName(e.target.checked)}
                    />
                    <span>Use as display name</span>
                  </label>
                </div>

                <div className="med-form-field">
                  <label className="med-form-label" htmlFor="displayName">
                    Display name <span className="med-form-optional">(optional)</span>
                  </label>
                  <input
                    id="displayName"
                    className="med-form-input"
                    type="text"
                    placeholder="e.g. Pepper's heart pill"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    readOnly={useAsDisplayName}
                    disabled={useAsDisplayName}
                  />
                  <span className="med-form-helper">How it appears in reminders.</span>
                </div>

              </div>
            </div>

            {/* ── Section 2: Dosage ───────────────────────────────── */}
            <div className="med-form-section">
              <div className="med-form-section-header">
                <span className="med-form-section-title">Dosage</span>
              </div>
              <div className="med-form-section-body">

                <div className="med-form-field">
                  <label className="med-form-label" htmlFor="format">
                    Format <span className="med-form-required">*</span>
                  </label>
                  <select
                    id="format"
                    className={`med-form-select${errors.format ? ' med-form-input--error' : ''}`}
                    value={format}
                    onChange={e => {
                      setFormat(e.target.value);
                      setErrors(prev => ({ ...prev, format: null }));
                    }}
                  >
                    <option value="">Select format…</option>
                    {FORMAT_OPTIONS.map(f => (
                      <option key={f} value={f}>
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </option>
                    ))}
                  </select>
                  {errors.format && (
                    <span className="med-form-error">{errors.format}</span>
                  )}
                </div>

                <div className="med-form-field">
                  <label className="med-form-label" htmlFor="quantity">
                    Quantity <span className="med-form-required">*</span>
                  </label>
                  <input
                    id="quantity"
                    className={`med-form-input med-form-input--short${errors.quantity ? ' med-form-input--error' : ''}`}
                    type="number"
                    min="0.01"
                    step="0.5"
                    placeholder="e.g. 1"
                    value={quantity}
                    onChange={e => {
                      setQuantity(e.target.value);
                      setErrors(prev => ({ ...prev, quantity: null }));
                    }}
                  />
                  {errors.quantity && (
                    <span className="med-form-error">{errors.quantity}</span>
                  )}
                </div>

              </div>
            </div>

            {/* ── Section 3: Schedule ─────────────────────────────── */}
            <div className="med-form-section">
              <div className="med-form-section-header">
                <span className="med-form-section-title">Schedule</span>
              </div>
              <div className="med-form-section-body">

                {/* Frequency picker */}
                <div className="med-form-field">
                  <label className="med-form-label">
                    Frequency <span className="med-form-required">*</span>
                  </label>
                  <div className="med-form-freq-group">
                    {FREQUENCY_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`med-form-freq-btn${frequency === opt.value ? ' med-form-freq-btn--active' : ''}`}
                        onClick={() => handleFrequencyChange(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time slots */}
                {showTimeFields && (
                  <div className="med-form-field">
                    <label className="med-form-label">Time(s)</label>
                    <div className="med-form-slots">
                      {timeSlots.map((slot, i) => (
                        <div key={i} className="med-form-slot-row">
                          <select
                            className="med-form-select med-form-select--time"
                            value={slot.hour}
                            onChange={e => updateSlot(i, 'hour', parseInt(e.target.value, 10))}
                            aria-label="Hour"
                          >
                            {HOURS.map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                          <span className="med-form-slot-colon">:</span>
                          <select
                            className="med-form-select med-form-select--time"
                            value={slot.minute}
                            onChange={e => updateSlot(i, 'minute', e.target.value)}
                            aria-label="Minute"
                          >
                            {MINUTES.map(m => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                          <div className="med-form-period-toggle">
                            <button
                              type="button"
                              className={`med-form-period-btn${slot.period === 'AM' ? ' med-form-period-btn--active' : ''}`}
                              onClick={() => updateSlot(i, 'period', 'AM')}
                            >
                              AM
                            </button>
                            <button
                              type="button"
                              className={`med-form-period-btn${slot.period === 'PM' ? ' med-form-period-btn--active' : ''}`}
                              onClick={() => updateSlot(i, 'period', 'PM')}
                            >
                              PM
                            </button>
                          </div>
                          {timeSlots.length > 1 && (
                            <button
                              type="button"
                              className="med-form-slot-remove"
                              onClick={() => removeSlot(i)}
                              aria-label="Remove time"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {errors.times && (
                      <span className="med-form-error">{errors.times}</span>
                    )}
                    <button
                      type="button"
                      className="med-form-add-time"
                      onClick={addSlot}
                    >
                      + Add another time
                    </button>
                    {tzLabel && (
                      <p className="med-form-tz-note">🕐 {tzLabel}</p>
                    )}
                  </div>
                )}

                {/* Day chips */}
                {showDayChips && (
                  <div className="med-form-field">
                    <label className="med-form-label">Days</label>
                    <div className="med-form-day-chips">
                      {DAYS.map(day => (
                        <button
                          key={day.value}
                          type="button"
                          className={`med-form-day-chip${selectedDays.includes(day.value) ? ' med-form-day-chip--selected' : ''}`}
                          onClick={() => toggleDay(day.value)}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                    {errors.days && (
                      <span className="med-form-error">{errors.days}</span>
                    )}
                  </div>
                )}

                {frequency === 'as_needed' && (
                  <p className="med-form-as-needed-note">
                    No reminders will fire. This medication will appear in the "As needed" section of the dashboard for manual logging.
                  </p>
                )}

              </div>
            </div>

            {/* ── Section 4: Optional details ─────────────────────── */}
            <div className="med-form-section">
              <button
                type="button"
                className="med-form-optional-toggle"
                onClick={() => setShowOptional(prev => !prev)}
              >
                <span className={`med-form-toggle-arrow${showOptional ? ' med-form-toggle-arrow--open' : ''}`}>▸</span>
                Add strength or instructions
              </button>

              {showOptional && (
                <div className="med-form-section-body">

                  {/* Strength */}
                  <div className="med-form-field">
                    <label className="med-form-label">Strength</label>
                    <div className="med-form-strength-row">
                      <input
                        className="med-form-input med-form-input--short"
                        type="number"
                        min="0"
                        step="any"
                        placeholder="e.g. 10"
                        value={strengthAmount}
                        onChange={e => setStrengthAmount(e.target.value)}
                      />
                      <select
                        className="med-form-select med-form-select--unit"
                        value={strengthUnit}
                        onChange={e => {
                          setStrengthUnit(e.target.value);
                          if (e.target.value !== 'Other') setStrengthUnitCustom('');
                        }}
                      >
                        <option value="">Unit…</option>
                        {STRENGTH_UNIT_OPTIONS.map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                      {strengthUnit === 'Other' && (
                        <input
                          className="med-form-input med-form-input--unit-custom"
                          type="text"
                          placeholder="Unit name"
                          value={strengthUnitCustom}
                          onChange={e => setStrengthUnitCustom(e.target.value)}
                        />
                      )}
                    </div>
                  </div>

                  {/* Special instructions */}
                  <div className="med-form-field">
                    <label className="med-form-label" htmlFor="instructions">
                      Special instructions
                    </label>
                    <textarea
                      id="instructions"
                      className="med-form-textarea"
                      placeholder="e.g. Give with food"
                      rows={3}
                      value={specialInstructions}
                      onChange={e => setSpecialInstructions(e.target.value)}
                    />
                  </div>

                  {/* Start / end date */}
                  <div className="med-form-field">
                    <label className="med-form-label">Start date</label>
                    <input
                      className="med-form-input med-form-input--date"
                      type="date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="med-form-field">
                    <label className="med-form-label">End date</label>
                    <input
                      className="med-form-input med-form-input--date"
                      type="date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                    />
                  </div>

                </div>
              )}
            </div>

            {/* ── Footer ──────────────────────────────────────────── */}
            <div className="med-form-footer">
              {saveError && (
                <p className="med-form-save-error">{saveError}</p>
              )}
              {saveSuccess && (
                <p className="med-form-save-success">Saved!</p>
              )}
              {onStop && !confirmingStop && (
                <button
                  type="button"
                  className="med-form-stop"
                  onClick={() => setConfirmingStop(true)}
                >
                  Stop medication
                </button>
              )}
              {confirmingStop && (
                <div className="med-form-stop-confirm">
                  <p className="med-form-stop-confirm-text">
                    Stop {medicationName}? This removes it from the schedule.
                  </p>
                  <div className="med-form-stop-confirm-actions">
                    <button
                      type="button"
                      className="med-form-stop-confirm-btn"
                      onClick={async () => {
                        setIsSaving(true);
                        setSaveError(null);
                        try {
                          await onStop();
                          navigate('/meds');
                        } catch {
                          setSaveError('Could not stop medication. Try again.');
                        } finally {
                          setIsSaving(false);
                        }
                      }}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Stopping…' : 'Confirm stop'}
                    </button>
                    <button
                      type="button"
                      className="med-form-stop-cancel-btn"
                      onClick={() => setConfirmingStop(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              <button
                type="submit"
                className="med-form-submit"
                disabled={isSaving || saveSuccess}
              >
                {isSaving ? 'Saving…' : 'Save Medication'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}
